import { Component } from '@theme/component';

/**
 * A custom element that renders a poster-first ambient video.
 * Sources stay deferred until near the viewport, then play muted;
 * playback pauses when leaving the viewport. Respects reduced motion.
 *
 * @typedef {object} Refs
 * @property {HTMLElement[]} videoSources - The video sources.
 * @property {HTMLVideoElement} videoElement - The video element.
 * @property {HTMLButtonElement} [playToggle] - The optional play/pause button.
 *
 * @extends Component<Refs>
 */
export class VideoBackgroundComponent extends Component {
  requiredRefs = ['videoSources', 'videoElement'];

  /** @type {IntersectionObserver | null} */
  #observer = null;

  /** @type {boolean} */
  #sourcesAttached = false;

  /** @type {boolean} */
  #isIntersecting = false;

  /** @type {boolean} */
  #shouldPlay = false;

  /** @type {boolean} */
  #hovering = false;

  /** @type {MediaQueryList[]} */
  #sourceMediaQueries = [];

  /** @type {MediaQueryList} */
  #reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)');

  connectedCallback() {
    super.connectedCallback();

    const { videoElement } = this.refs;
    this.#shouldPlay = videoElement instanceof HTMLVideoElement && videoElement.autoplay;

    this.#reducedMotion.addEventListener('change', this.#handleReducedMotionChange);
    this.#setupSourceMediaQueries();
    this.#setupPlayToggle();
    this.#setupHoverControls();

    if (this.#reducedMotion.matches) return;

    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#activate();
          } else {
            this.#pause();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );

    this.#observer.observe(this);
  }

  disconnectedCallback() {
    this.#observer?.disconnect();
    this.#observer = null;
    this.#isIntersecting = false;
    for (const mediaQuery of this.#sourceMediaQueries) {
      mediaQuery.removeEventListener('change', this.#handleSourceMediaChange);
    }
    this.#sourceMediaQueries = [];
    this.#reducedMotion.removeEventListener('change', this.#handleReducedMotionChange);

    const { videoElement } = this.refs;
    if (videoElement instanceof HTMLVideoElement) {
      videoElement.removeEventListener('play', this.#syncPlayToggle);
      videoElement.removeEventListener('pause', this.#syncPlayToggle);
      videoElement.removeEventListener('ended', this.#syncPlayToggle);
      videoElement.removeEventListener('play', this.#syncHoverControls);
      videoElement.removeEventListener('pause', this.#syncHoverControls);
      videoElement.removeEventListener('ended', this.#syncHoverControls);
    }

    this.removeEventListener('pointerenter', this.#onPointerEnter);
    this.removeEventListener('pointerleave', this.#onPointerLeave);

    super.disconnectedCallback();
  }

  #setupPlayToggle() {
    const { videoElement, playToggle } = this.refs;

    if (!(playToggle instanceof HTMLElement) || !(videoElement instanceof HTMLVideoElement)) return;

    videoElement.addEventListener('play', this.#syncPlayToggle);
    videoElement.addEventListener('pause', this.#syncPlayToggle);
    videoElement.addEventListener('ended', this.#syncPlayToggle);
    this.#syncPlayToggle();
  }

  #setupHoverControls() {
    const { videoElement } = this.refs;

    if (!(videoElement instanceof HTMLVideoElement) || !videoElement.hasAttribute('data-hover-controls')) return;

    this.addEventListener('pointerenter', this.#onPointerEnter);
    this.addEventListener('pointerleave', this.#onPointerLeave);
    videoElement.addEventListener('play', this.#syncHoverControls);
    videoElement.addEventListener('pause', this.#syncHoverControls);
    videoElement.addEventListener('ended', this.#syncHoverControls);
    this.#syncHoverControls();
  }

  #onPointerEnter = () => {
    this.#hovering = true;
    this.#syncHoverControls();
  };

  #onPointerLeave = () => {
    this.#hovering = false;
    this.#syncHoverControls();
  };

  #syncHoverControls = () => {
    const { videoElement } = this.refs;

    if (!(videoElement instanceof HTMLVideoElement)) return;

    videoElement.toggleAttribute('controls', !videoElement.paused && this.#hovering);
  };

  #syncPlayToggle = () => {
    const { videoElement, playToggle } = this.refs;

    if (!(playToggle instanceof HTMLElement) || !(videoElement instanceof HTMLVideoElement)) return;

    const isPlaying = !videoElement.paused;
    playToggle.querySelector('.ambient-video__play-icon')?.classList.toggle('hidden', isPlaying);
    playToggle.querySelector('.ambient-video__pause-icon')?.classList.toggle('hidden', !isPlaying);

    const label = isPlaying ? playToggle.dataset.pauseLabel : playToggle.dataset.playLabel;
    if (label) playToggle.setAttribute('aria-label', label);
  };

  /**
   * Plays or pauses the video from the play/pause toggle. A video paused here stays
   * paused when it scrolls back into view.
   */
  togglePlayback = () => {
    const { videoElement } = this.refs;

    if (!(videoElement instanceof HTMLVideoElement)) return;

    if (videoElement.paused) {
      this.#shouldPlay = true;
      this.#activate();
      return;
    }

    this.#shouldPlay = false;
    videoElement.pause();
  };

  #setupSourceMediaQueries() {
    const { videoSources } = this.refs;
    const sources = Array.isArray(videoSources) ? videoSources : videoSources ? [videoSources] : [];
    const mediaQueries = new Set(
      sources
        .filter((source) => source instanceof HTMLSourceElement && source.media)
        .map((source) => /** @type {HTMLSourceElement} */ (source).media)
    );

    for (const query of mediaQueries) {
      const mediaQuery = window.matchMedia(query);
      mediaQuery.addEventListener('change', this.#handleSourceMediaChange);
      this.#sourceMediaQueries.push(mediaQuery);
    }
  }

  #handleSourceMediaChange = () => {
    if (this.#isIntersecting) this.#activate();
  };

  #handleReducedMotionChange = () => {
    if (this.#reducedMotion.matches) {
      this.#pause();
      this.#observer?.disconnect();
      return;
    }

    if (!this.isConnected) return;

    this.#observer?.disconnect();
    this.#observer = new IntersectionObserver(
      (entries) => {
        for (const entry of entries) {
          if (entry.isIntersecting) {
            this.#activate();
          } else {
            this.#pause();
          }
        }
      },
      { rootMargin: '200px 0px' }
    );
    this.#observer.observe(this);
  };

  #activate() {
    const { videoSources, videoElement } = this.refs;

    if (!(videoElement instanceof HTMLVideoElement)) return;

    this.#isIntersecting = true;
    const sources = Array.isArray(videoSources) ? videoSources : videoSources ? [videoSources] : [];
    let sourcesChanged = false;

    for (const source of sources) {
      if (!(source instanceof HTMLSourceElement)) continue;

      const isActiveSource = !source.media || window.matchMedia(source.media).matches;
      const nextSource = isActiveSource ? source.dataset.videoSource : '';
      const currentSource = source.getAttribute('src') || '';

      if (nextSource && nextSource !== currentSource) {
        source.setAttribute('src', nextSource);
        sourcesChanged = true;
      } else if (!nextSource && currentSource) {
        source.removeAttribute('src');
        sourcesChanged = true;
      }
    }

    if (!this.#sourcesAttached || sourcesChanged) {
      this.#sourcesAttached = true;
      videoElement.load();
    }

    if (this.#shouldPlay) {
      videoElement.play()?.catch(() => {});
    }
  }

  #pause() {
    const { videoElement } = this.refs;
    this.#isIntersecting = false;

    if (videoElement instanceof HTMLVideoElement) {
      videoElement.pause();
    }
  }
}

if (!customElements.get('video-background-component')) {
  customElements.define('video-background-component', VideoBackgroundComponent);
}
